import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Header.css";
import { Container } from "../HtmlTags/Container/Container";
import { InputField } from "../HtmlTags/InputField/InputField";
import { RawButton } from "../HtmlTags/Button/RawButton";
import { ReactComponent as SearchIcon } from "../../../assets/images/search_icon.svg";
import { ReactComponent as BellIcon } from "../../../assets/images/svgIcons/bell-icon.svg";
import { ReactComponent as ChatIcon } from "../../../assets/images/svgIcons/chat-icon.svg";
import { Menu, X, MapPin, User } from "lucide-react";
import { Filters } from "./components/Filter";
import { DrawerAnimation, HeaderProps } from "./types/types";
import { Button } from "../HtmlTags/Button/Button";
import { ListFilter } from "lucide-react";
import { useAppSelector } from "../../../hooks/redux";
import useCommonData from "../../../hooks/useCommon";
import {
  setSearchQuery,
  setSearchFilterOpen,
  setSelectedCategory,
  setSelectedCondition,
  setMaxDistance,
  searchSelector,
  searchItems,
} from "../../../store/slices/searchSlice";
import { setLocation, locationSelector } from "../../../store/slices/locationSlice";
import { selectUnreadNotificationCount } from "../../../store/slices/notificationSlice";
import ProfileAvatar from "../../../features/Profile/components/ProfileAvatar/ProfileAvatar";
import { debounce } from "../../../utils/helpers";
import { Heading } from "../HtmlTags/Heading/Heading";
import { Paragraph } from "../HtmlTags/Paragraph/Paragraph";
import { Span } from "../HtmlTags/Span/Span";
import { PopupModalRef } from "../Modal/Modal";
import { useLocationSelector } from "../../feature/LocationSelector/hooks/useLocationSelector";
import { LocationData } from "../../feature/LocationSelector/types";
import { ERROR_MESSAGES, SHARE_LABELS, SORT_KEYS, DEFAULT_SEARCH_DISTANCE } from "../../../utils/constants";
import LocationModal from "../../feature/LocationModal/LocationModal";
import { ZelpyeLogo } from "../ZelpyeLogo/ZelpyeLogo";
import { ZELPYE_LOGO_VARIANT } from "../ZelpyeLogo/ZelpyeLogo.constants";
import { ITEMS_PATH } from "../../../constants";
import { savePendingDeepLink } from "../../../utils/deepLink";

const Header: React.FC<HeaderProps> = ({ isPublicLayout = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    isAdmin,
    isAdminPanel,
    showAppDashboard,
    dispatch,
    categoryOptions,
    searchConditionFilterOptions,
    isBlockedByAdmin,
    userProfile,
    userToken
  } = useCommonData();
  const {
    searchQuery,
    searchInputValue,
    isSearchFilterOpen,
    debouncedSearchResults,
    loading,
    error,
    selectedCategory,
    selectedCondition,
    maxDistance,
  } = useAppSelector(searchSelector);
  const chatUnreadCount = useAppSelector((state) => state.chat.unreadCount);
  const notificationUnreadCount = useAppSelector(selectUnreadNotificationCount);

  const isUnauthenticatedPublicLayout = isPublicLayout && !userToken.accessToken;
  const isGuest = !userToken.accessToken;
  const shareItemMatch = pathname.match(
    new RegExp(`^${ITEMS_PATH}/share/([^/]+)`)
  );
  const shareItemReturnPath = shareItemMatch
    ? `${ITEMS_PATH}/${shareItemMatch[1]}`
    : null;
  const publicLoginHref = shareItemReturnPath
    ? `/auth/login?redirect=${encodeURIComponent(shareItemReturnPath)}`
    : "/auth/login";
  const handlePublicLoginClick = () => {
    if (shareItemReturnPath) {
      savePendingDeepLink(shareItemReturnPath);
    }
  };
  const locationState = useAppSelector(locationSelector);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [inputValue, setInputValue] = useState(searchInputValue || "");
  const [drawerAnimation, setDrawerAnimation] =
    useState<DrawerAnimation>("drawer-open");
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    locationState.location
  );
  const searchRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const locationModalRef = useRef<PopupModalRef>(null);

  useEffect(() => {
    setInputValue(searchInputValue);
  }, [searchInputValue]);

  useEffect(() => {
    if (locationState.location) {
      setSelectedLocation(locationState.location);
    }
  }, [locationState.location]);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setIsHeaderFixed(scrollTop > headerHeight / 2);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!showDropdown) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Element | null;
      if (target && target.closest(".search-container")) {
        return;
      }
      setShowDropdown(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showDropdown]);

  const runSearch = useCallback(
    async (
      keyword: string,
      overrides?: {
        category?: string[];
        condition?: string[];
        distance?: number;
      }
    ) => {
      const trimmedKeyword = keyword.trim();
      if (!trimmedKeyword) {
        setShowDropdown(false);
        return;
      }

      await dispatch(
        searchItems({
          keyword: trimmedKeyword,
          radiusInKm: Math.max(10, overrides?.distance ?? maxDistance),
          itemCondition: overrides?.condition ?? selectedCondition,
          itemCategory: overrides?.category ?? selectedCategory,
          sortKey: SORT_KEYS.NEAR_ME,
          itemsPerPage: 10,
          pageNo: 1,
        })
      );
      setShowDropdown(true);
    },
    [dispatch, maxDistance, selectedCondition, selectedCategory]
  );

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    debounce(async (keyword: string) => {
      await runSearch(keyword);
    }, 500),
    [runSearch]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      dispatch(setSearchQuery(value));
      if (!value.trim()) {
        setShowDropdown(false);
      } else {
        debouncedSearch(value);
      }
    },
    [dispatch, debouncedSearch]
  );

  const handleSearchButtonClick = useCallback(() => {
    if (inputValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(inputValue.trim())}`);
      setShowDropdown(false);
    }
  }, [inputValue, navigate]);

  const handleItemClick = useCallback((item: any) => {
    navigate(`/search?q=${encodeURIComponent(inputValue.trim())}`);
    setShowDropdown(false);
  }, [inputValue, navigate]);
  const onLocationChange = useCallback(
    (locationData: LocationData) => {
      setSelectedLocation(locationData);
      dispatch(setLocation(locationData));
      locationModalRef.current?.closeModal();
    },
    [dispatch]
  );

  const {
    location,
    isLoadingLocation,
    modalRef: mapPickerModalRef,
    fetchUserLocation,
    openMapPicker,
    handleMapSelect,
  } = useLocationSelector(selectedLocation, onLocationChange);

  const handleLocationButtonClick = useCallback(() => {
    locationModalRef.current?.openModal();
  }, []);

  const toggleSearchFilter = useCallback(() => {
    dispatch(setSearchFilterOpen(!isSearchFilterOpen));
  }, [isSearchFilterOpen, dispatch]);

  const handleCategoryChange = useCallback(
    (category: string[]) => {
      dispatch(setSelectedCategory(category));
      if (inputValue.trim()) {
        runSearch(inputValue, { category });
      }
    },
    [dispatch, inputValue, runSearch]
  );

  const handleConditionChange = useCallback(
    (condition: string[]) => {
      dispatch(setSelectedCondition(condition));
      if (inputValue.trim()) {
        runSearch(inputValue, { condition });
      }
    },
    [dispatch, inputValue, runSearch]
  );

  const handleDistanceChange = useCallback(
    (newDistance: number) => {
      dispatch(setMaxDistance(Math.max(10, newDistance)));
      if (inputValue.trim()) {
        runSearch(inputValue, { distance: newDistance });
      }
    },
    [dispatch, inputValue, runSearch]
  );

  const hasActiveFilters =
    selectedCategory.length > 0 ||
    selectedCondition.length > 0 ||
    maxDistance !== DEFAULT_SEARCH_DISTANCE;

  const handleResetFilters = useCallback(() => {
    dispatch(setSelectedCategory([]));
    dispatch(setSelectedCondition([]));
    dispatch(setMaxDistance(DEFAULT_SEARCH_DISTANCE));
    if (inputValue.trim()) {
      runSearch(inputValue, {
        category: [],
        condition: [],
        distance: DEFAULT_SEARCH_DISTANCE,
      });
    }
  }, [dispatch, inputValue, runSearch]);

  const toggleDrawer = useCallback(() => {
    if (isDrawerOpen) {
      setDrawerAnimation("drawer-closing");
      setTimeout(() => setIsDrawerOpen(false), 300);
    } else {
      setDrawerAnimation("drawer-open");
      setIsDrawerOpen(true);
    }
  }, [isDrawerOpen]);
  const renderSearchInput = useCallback(
    () => (
      <Container className="search-container">
        <InputField
          ref={searchRef}
          placeholder="Search"
          value={inputValue}
          onChange={handleInputChange}
          showSearchButton
          icon={<SearchIcon style={{ cursor: "pointer" }} />}
          inputIconStyle="icon-outside"
          className="search-input"
          buttonClick={handleSearchButtonClick}
        />

        {showDropdown && inputValue.trim() && (
          <Container className="search-dropdown">
            {loading ? (
              <Paragraph>Loading...</Paragraph>
            ) : error ? (
              <Paragraph className="error-message">
                {ERROR_MESSAGES.SOMETHING_WENT_WRONG}
              </Paragraph>
            ) : (() => {
              const resultsArray = Array.isArray(debouncedSearchResults) ? debouncedSearchResults : [];
              if (resultsArray.length > 0) {
                return resultsArray.map((item: any) => (
                  <Container
                    key={item.itemId}
                    className="search-dropdown-item"
                    onClick={() => handleItemClick(item)}
                  >
                    <Paragraph className="item-name">{item.name}</Paragraph>
                  </Container>
                ));
              } else {
                return <Paragraph>No items found</Paragraph>;
              }
            })()}
          </Container>
        )}
      </Container>
    ),
    [
      inputValue,
      handleInputChange,
      showDropdown,
      loading,
      error,
      debouncedSearchResults,
      handleSearchButtonClick,
      handleItemClick
    ]
  );

  const renderFilterButton = useCallback(
    (className?: string) => (
      <Button
        palette="secondary"
        size="small"
        colorScheme="medium"
        hasIcon={true}
        className={`header-filter-btn ${isSearchFilterOpen ? "active" : ""} ${className || ""}`}
        type="button"
        aria-label={SHARE_LABELS.FILTER_LISTING}
        onClick={toggleSearchFilter}
      >
        <ListFilter />
      </Button>
    ),
    [isSearchFilterOpen, toggleSearchFilter]
  );

  const renderLocationButton = useCallback(
    (className?: string) => {
      const displayLocation = location?.address || SHARE_LABELS.SELECT_LOCATION;
      return (
        <>
          <RawButton
            className={`location-button ${className || ""}`}
            onClick={handleLocationButtonClick}
          >
            <MapPin size={16} />
            <Span className="location-text">{displayLocation}</Span>
          </RawButton>
          <LocationModal
            locationModalRef={locationModalRef}
            location={location}
            fetchUserLocation={fetchUserLocation}
            isLoadingLocation={isLoadingLocation}
            openMapPicker={openMapPicker}
            handleMapSelect={handleMapSelect}
            mapPickerModalRef={mapPickerModalRef}
          />
        </>
      );
    },
    [
      location,
      isLoadingLocation,
      fetchUserLocation,
      openMapPicker,
      handleMapSelect,
      handleLocationButtonClick,
      mapPickerModalRef,
    ]
  );

  const renderFilters = useCallback(
    () => (
      <Filters
        searchText={searchQuery}
        maxDistance={maxDistance}
        setMaxDistance={handleDistanceChange}
        categoryOptions={categoryOptions}
        conditionOptions={searchConditionFilterOptions}
        selectedCategory={selectedCategory}
        selectedCondition={selectedCondition}
        onCategoryChange={handleCategoryChange}
        onConditionChange={handleConditionChange}
        onResetFilters={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
      />
    ),
    [
      searchQuery,
      maxDistance,
      categoryOptions,
      searchConditionFilterOptions,
      selectedCategory,
      selectedCondition,
      handleDistanceChange,
      handleCategoryChange,
      handleConditionChange,
      handleResetFilters,
      hasActiveFilters,
    ]
  );

  const renderLogo = () => (
    <Link to="/" className="logo header-logo">
      <ZelpyeLogo variant={ZELPYE_LOGO_VARIANT.TEXT_LOGO} />
    </Link>
  );

  const renderLeftSection = () => (
    <Container className="left-section">
      {!isAdminPanel && !isBlockedByAdmin && !isUnauthenticatedPublicLayout && (
        <RawButton className="hamburger" onClick={toggleDrawer}>
          <Menu />
        </RawButton>
      )}
      {renderLogo()}
      {(!isAdminPanel && !isUnauthenticatedPublicLayout) && renderLocationButton("desktop-only")}
    </Container>
  );

  const renderCenterSection = () => {
    if (isAdminPanel) {
      return <Heading variant="h2" className="admin-panel-heading">Admin Panel</Heading>;
    }
    if (isBlockedByAdmin || isUnauthenticatedPublicLayout) {
      return null;
    }
    return (
      <Container className="center-section desktop-only">
        {renderSearchInput()}
        <Container className="header-filter-btn-wrap">
          {renderFilterButton()}
        </Container>
      </Container>
    );
  };

  const renderGuestAuthLinks = () => (
    <Link
      to={publicLoginHref}
      onClick={handlePublicLoginClick}
      className="header-guest-login-btn"
      aria-label={SHARE_LABELS.LOGIN}
    >
      {SHARE_LABELS.LOGIN}
    </Link>
  );

  const renderRightSection = () => (
    <Container className="right-section">
      {isGuest ? (
        isUnauthenticatedPublicLayout ? (
          <Link
            to={publicLoginHref}
            onClick={handlePublicLoginClick}
            className="auth-link"
            aria-label={SHARE_LABELS.LOGIN}
          >
            <User size={24} strokeWidth={1.5} color="var(--primary-shade1)" />
          </Link>
        ) : (
          renderGuestAuthLinks()
        )
      ) : (
        <>
          {!isAdminPanel && (
            <>
              <Link
                to="/notifications"
                className="icon-button notification-link"
                aria-label={
                  notificationUnreadCount > 0
                    ? SHARE_LABELS.NOTIFICATIONS_UNREAD
                    : SHARE_LABELS.NOTIFICATIONS
                }
              >
                <BellIcon />
                {notificationUnreadCount > 0 && (
                  <span className="header-nav-unread-badge" aria-hidden />
                )}
              </Link>
              <Link
                to="/chat"
                className="icon-button chat-link"
                aria-label={
                  chatUnreadCount > 0 ? SHARE_LABELS.MESSAGES_UNREAD : SHARE_LABELS.MESSAGES
                }
              >
                <ChatIcon />
                {chatUnreadCount > 0 && (
                  <span className="header-nav-unread-badge" aria-hidden />
                )}
              </Link>
            </>
          )}
          <Link to="/profile">
            <ProfileAvatar
              profileName={userProfile?.userName}
              profileImg={userProfile?.image}
            />
          </Link>
        </>
      )}
    </Container>
  );

  const renderDrawer = () =>
    !isBlockedByAdmin && isDrawerOpen && (
      <Container className={`drawer-container ${drawerAnimation}`}>
        <RawButton className="drawer-close-button" onClick={toggleDrawer}>
          <X size={24} />
        </RawButton>

        {renderSearchInput()}
        {renderLocationButton("drawer-dropdown")}
        {!isAdminPanel && renderFilters()}
      </Container>
    );


  return (
    <>
      <header
        ref={headerRef}
        className={`header-wrapper ${isHeaderFixed ? "header-fixed" : ""}`}
      >
        <Container className="header">
          {renderLeftSection()}
          {renderCenterSection()}
          {renderRightSection()}
        </Container>

        {isSearchFilterOpen && !isBlockedByAdmin && !isAdminPanel && (
          <Container className="header-second-row desktop-only">
            {renderFilters()}
          </Container>
        )}

        {renderDrawer()}
        {isAdmin && (
          <>
            {!isAdminPanel && !isUnauthenticatedPublicLayout && <Link to="/admin" className="admin-panel-button">
              Admin Panel
            </Link>}
            {!showAppDashboard && !isUnauthenticatedPublicLayout && <Link to="/" className="admin-panel-button">
              App Dashboard
            </Link>}
          </>
        )}
      </header>
      {isHeaderFixed && (
        <Container
          style={{ height: headerRef.current?.offsetHeight || 0 }}
          decorativeContainer={true}
        />
      )}
    </>
  );
};

export default Header;

/**
 * Header component for the main application navigation and search functionality
 *
 * This component provides the main header interface with search functionality, location selection,
 * filtering options, and navigation elements. It includes responsive design with mobile drawer
 * functionality and scroll-based header positioning.
 *
 * Features:
 * - Search input with real-time query updates and navigation
 * - Location dropdown for area selection
 * - Advanced filters for item search (category, condition, distance)
 * - Mobile-responsive drawer menu
 * - Fixed header positioning on scroll
 * - Notification and chat icons
 * - Profile avatar with navigation
 *
 * @component
 * @param {HeaderProps} props - Component props (currently empty interface but prepared for future props)
 *
 * @example
 * ```tsx
 * <Header />
 * ```
 *
 * @returns {JSX.Element} Complete header component with search, navigation, and filtering capabilities
 */
